import { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, Reveal, Overline } from "../components/Kinetic";
import { FileText, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RessourcesPage = () => {
  const { t, lang } = useLanguage();
  useSEO(t("ressources.metaTitle"), t("ressources.metaDesc"));
  const categories = t("ressources.categories");
  const catKeys = t("ressourcesKeys");
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  const docsByCat = (key) => docs.filter((d) => d.category === key);

  return (
    <div data-testid="ressources-page">
      <PageHero
        overline={t("ressources.overline")}
        titleLines={t("ressources.titleLines")}
        intro={t("ressources.intro")}
        testid="ressources"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-px bg-graphite/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="ressources-grid">
            {categories.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.04} className="group bg-bone p-8 transition-colors duration-300 hover:bg-white">
                <div className="flex items-start justify-between">
                  <FileText className="h-5 w-5 text-forest" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-soft/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h2 className="mt-6 font-display text-xl md:text-2xl font-light tracking-tight text-graphite">{c.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-graphite-soft">{c.text}</p>
                <div className="mt-6 border-t border-graphite/10 pt-4" data-testid={`ressource-status-${i}`}>
                  {docsByCat(catKeys[i]).length === 0 ? (
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-graphite-soft/70">
                      {t("ressources.emptyLabel")}
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {docsByCat(catKeys[i]).map((d) => (
                        <li key={d.id}>
                          <a
                            href={`${API}/documents/${d.id}/file`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-start gap-2 text-sm text-forest underline-offset-4 transition-colors hover:underline"
                            data-testid={`doc-download-${d.id}`}
                          >
                            <Download className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{lang === "en" && d.title_en ? d.title_en : d.title_fr}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 max-w-3xl border-l-2 border-forest pl-6">
              <Overline>{t("nav.transparence")}</Overline>
              <p className="mt-3 text-sm leading-relaxed text-graphite-soft">{t("ressources.note")}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default RessourcesPage;
